import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Card,
  Form,
  Select,
  Typography,
  Empty,
  Space,
  Tag,
  Descriptions,
  Alert,
  Spin,
  Divider,
  Button,
  Input,
  Modal,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
  ExperimentOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi, {
  AbcdeConclusion,
  type AbcdeEvaluation,
  type DermatoscopyExam,
  type VideoDermatoscopyAi,
  AiClassification,
  ScreeningConclusion,
  type DermatoScreeningResult,
} from '../../../../api/dermatoApi';

const { Text, Paragraph } = Typography;

type ScreeningVerdict = 'NEGATIVE' | 'SUSPECT' | 'POSITIVE' | 'INCONCLUSIVE';

interface ScreeningSummary {
  verdict: ScreeningVerdict;
  riskFactors: number;
  abcde?: AbcdeEvaluation;
  derm?: DermatoscopyExam;
  ai?: VideoDermatoscopyAi;
}

const computeSummary = (
  abcde: AbcdeEvaluation | null,
  derm: DermatoscopyExam | null,
  ai: VideoDermatoscopyAi | null,
  riskFactorsCount: number
): ScreeningSummary => {
  if (!abcde && !derm && !ai) {
    return { verdict: 'INCONCLUSIVE', riskFactors: riskFactorsCount };
  }
  if (
    ai?.aiClassification === AiClassification.RISK_HIGH ||
    (ai?.aiProbability ?? 0) >= 70 ||
    abcde?.conclusion === AbcdeConclusion.SUSPICIOUS_LESION ||
    derm?.result === 'SUSPICIOUS_LESION'
  ) {
    return { verdict: 'POSITIVE', riskFactors: riskFactorsCount, abcde: abcde ?? undefined, derm: derm ?? undefined, ai: ai ?? undefined };
  }
  if (
    abcde?.conclusion === AbcdeConclusion.REQUIRES_DERMOSCOPY ||
    derm?.result === 'REQUIRES_FURTHER_EVALUATION' ||
    ai?.aiClassification === AiClassification.RISK_MODERATE
  ) {
    return { verdict: 'SUSPECT', riskFactors: riskFactorsCount, abcde: abcde ?? undefined, derm: derm ?? undefined, ai: ai ?? undefined };
  }
  return { verdict: 'NEGATIVE', riskFactors: riskFactorsCount, abcde: abcde ?? undefined, derm: derm ?? undefined, ai: ai ?? undefined };
};

const VERDICT_META: Record<ScreeningConclusion, { color: string; label: string; icon: React.ReactNode; recommendation: string }> = {
  NEGATIVE: {
    color: 'green',
    label: 'Negativ',
    icon: <CheckCircleOutlined />,
    recommendation: 'Continuă monitorizarea conform protocolului.',
  },
  SUSPECT: {
    color: 'orange',
    label: 'Suspect',
    icon: <QuestionCircleOutlined />,
    recommendation: 'Necesită investigații suplimentare (biopsie).',
  },
  POSITIVE: {
    color: 'red',
    label: 'Pozitiv — necesită investigații',
    icon: <ExclamationCircleOutlined />,
    recommendation: 'Recomandare fermă: biopsie excizională.',
  },
  INCONCLUSIVE: {
    color: 'default',
    label: 'Neconcludent',
    icon: <QuestionCircleOutlined />,
    recommendation: 'Completează pașii 4-6 (ABCDE, dermatoscopie, AI) pentru o concluzie.',
  },
  // 2.9.3 — refuzul participării („EXCLUS DIN PROGRAM") nu e verdict clinic:
  // ecranul afișează un mesaj dedicat, nu analiza de mai jos.
  SCREENING_REFUZAT: {
    color: 'red',
    label: 'Exclus din program',
    icon: <ExclamationCircleOutlined />,
    recommendation: 'Pacientul a refuzat participarea la programul de screening.',
  },
  // Finalizare fără leziuni (etapa 4 fără inventar): verdict negativ cu recontrol.
  NO_LESIONS: {
    color: 'green',
    label: 'Fără leziuni',
    icon: <CheckCircleOutlined />,
    recommendation: 'Screening fără leziuni — recontrol dermatologic conform intervalului stabilit.',
  },
};

const CONCLUSION_OPTIONS: { value: ScreeningConclusion; label: string }[] = [
  { value: ScreeningConclusion.NEGATIVE, label: 'Negativ' },
  { value: ScreeningConclusion.SUSPECT, label: 'Suspect' },
  { value: ScreeningConclusion.POSITIVE, label: 'Pozitiv — necesită investigații' },
  { value: ScreeningConclusion.INCONCLUSIVE, label: 'Neconcludent' },
];

const countTrue = (obj: Record<string, unknown> | undefined, keys: string[]) => {
  if (!obj) return 0;
  return keys.filter((k) => obj[k] === 'yes' || obj[k] === true).length;
};

/** Data recontrolului în format ro (pentru mesaje și statusul de revenire). */
const formatRecontrolDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('ro-RO') : '—';

/** Expunere zilnică la soare ≥ 1–3h sau > 3h = factor de risc. */
const isHighSunExposure = (v: unknown) =>
  v === '1_to_3_hours' || v === 'over_3_hours' || v === 'yes' || v === true;

const buildEvaluationSummary = (
  abcde: AbcdeEvaluation | null,
  derm: DermatoscopyExam | null,
  ai: VideoDermatoscopyAi | null,
  riskFactorsCount: number
): Record<string, unknown> => ({
  step2_riskFactors: riskFactorsCount,
  step4_abcde: abcde?.conclusion ?? null,
  step5_dermatoscopy: derm?.result ?? null,
  step6_ai: ai?.aiClassification ?? null,
  step6_aiProbability: ai?.aiProbability ?? null,
});

const Step8ScreeningResult = () => {
  const { patient, lesions, selectedLesionId, selectLesion, form, loading } = useDermatoWizard();
  const navigate = useNavigate();
  const [finalizing, setFinalizing] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [recontrolMonths, setRecontrolMonths] = useState<number>(12);
  const { user } = useAuthStore();
  const [abcde, setAbcde] = useState<AbcdeEvaluation | null>(null);
  const [derm, setDerm] = useState<DermatoscopyExam | null>(null);
  const [ai, setAi] = useState<VideoDermatoscopyAi | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [screening, setScreening] = useState<DermatoScreeningResult | null>(null);
  const [loadingScreening, setLoadingScreening] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creatingBiopsy, setCreatingBiopsy] = useState(false);

  // Override state
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideConclusion, setOverrideConclusion] = useState<ScreeningConclusion>(ScreeningConclusion.SUSPECT);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideError, setOverrideError] = useState<string | null>(null);

  // Persisted-but-editable fields (recomandare / observații)
  const [recommendation, setRecommendation] = useState<string>('');
  const [observations, setObservations] = useState<string>('');

  const evaluatedByRef = useRef<string>('');

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  // Rezumatul calculat (din pașii 1-7) pentru leziunea curentă
  useEffect(() => {
    if (!lesion?.id) {
      setAbcde(null);
      setDerm(null);
      setAi(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingData(true);
      try {
        const [a, d, v] = await Promise.all([
          dermatoApi.abcde.getByLesion(lesion.id!).catch(() => null),
          dermatoApi.dermatoscopy.getByLesion(lesion.id!).catch(() => null),
          dermatoApi.videoAi.getByLesion(lesion.id!).catch(() => null),
        ]);
        if (cancelled) return;
        setAbcde(a);
        setDerm(d);
        setAi(v);
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [lesion?.id]);

  // D2.3 — Încarcă verdictul persistat la nivel de FORMULAR (1:N per leziune vizată la upsert)
  useEffect(() => {
    if (!form?.id) {
      setScreening(null);
      setRecommendation('');
      setObservations('');
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoadingScreening(true);
      try {
        const s = await dermatoApi.screeningResult.getByForm(form.id);
        if (cancelled) return;
        setScreening(s);
        setRecommendation(s?.recommendation ?? '');
        setObservations(s?.observations ?? '');
      } catch {
        if (!cancelled) setScreening(null);
      } finally {
        if (!cancelled) setLoadingScreening(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [form?.id]);

  useEffect(() => {
    const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    evaluatedByRef.current = name || user?.email || 'medic';
  }, [user]);

  const riskFactorsCount = useMemo(() => {
    if (!form) return 0;
    const m = (form.medicalHistoryDetails ?? {}) as Record<string, unknown>;
    const f = (form.familyHistoryDetails ?? {}) as Record<string, unknown>;
    const u = (form.uvRiskFactors ?? {}) as Record<string, unknown>;
    return (
      countTrue(m, [
        'previousSkinCancer',
        'previousOtherCancer',
        'geneticMutation',
        'chronicDermatologicConditions',
        'autoimmuneOrImmunosuppression',
        'photosensitizingMedication',
      ]) +
      countTrue(f, ['familySkinCancer', 'familyGeneticDisease']) +
      countTrue(u, [
        'severeSunburns',
        'tanningWithoutSpf',
        'tanningSalonUse',
        'highUvResidence',
        'toxicChemicalExposure',
      ]) +
      (isHighSunExposure(u.sunExposureDaily) ? 1 : 0)
    );
  }, [form]);

  const summary = useMemo(
    () => computeSummary(abcde, derm, ai, riskFactorsCount),
    [abcde, derm, ai, riskFactorsCount]
  );

  /**
   * D2.3 — verdictul afișat efectiv:
   *  - dacă există verdict persistat cu override manual, folosim override-ul
   *  - altfel, verdictul calculat dinamic
   * `referralToBiopsy` urmează aceeași logică.
   *
   * 2.9.3 — refuzul participării (SCREENING_REFUZAT) nu e verdict clinic:
   * ecranul afișează mesajul „EXCLUS DIN PROGRAM" (vezi ramura de mai jos).
   */
  const persistedConclusion = screening?.screeningConclusion;
  const effectiveVerdict: ScreeningVerdict =
    persistedConclusion === ScreeningConclusion.SCREENING_REFUZAT || persistedConclusion === undefined
      ? summary.verdict
      : (String(persistedConclusion) as ScreeningVerdict);
  const effectiveReferralToBiopsy = screening?.referralToBiopsy ?? (summary.verdict === 'POSITIVE');
  const effectiveMeta = VERDICT_META[effectiveVerdict];

  /**
   * Persistă verdictul curent pe formular (upsert screening-result).
   * Dacă există deja un screening persistat cu isManualOverride=true, se
   * păstrează flag-ul și overrideReason; altfel, se salvează verdictul calculat.
   */
  const persist = async (): Promise<boolean> => {
    if (!form?.id) return true;
    if (summary.verdict === 'INCONCLUSIVE') {
      // Nu persistăm un verdict neconcludent când lipsesc datele de input.
      return true;
    }
    try {
      setSaving(true);
      const isOverride = screening?.isManualOverride === true;
      const conclusionToSave: ScreeningConclusion = isOverride
        ? screening!.screeningConclusion
        : (summary.verdict as ScreeningConclusion);
      const dto = {
        evaluationSummary: buildEvaluationSummary(abcde, derm, ai, riskFactorsCount),
        screeningConclusion: conclusionToSave,
        recommendation: recommendation || VERDICT_META[summary.verdict].recommendation,
        observations: observations || undefined,
        referralToBiopsy: effectiveReferralToBiopsy,
        isManualOverride: isOverride,
        overrideReason: isOverride ? screening!.overrideReason ?? undefined : undefined,
        evaluatedBy: evaluatedByRef.current || user?.email || 'medic',
      };
      const saved = await dermatoApi.screeningResult.upsert(form.id, dto);
      setScreening(saved);
      return true;
    } catch (e: any) {
      const apiMsg = e?.response?.data?.message;
      if (Array.isArray(apiMsg)) {
        message.error(apiMsg.join(', '));
      } else {
        message.error(apiMsg ?? 'Nu s-a putut salva verdictul de screening.');
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  /**
   * D2.3 — Override manual cu motiv obligatoriu. Backend-ul validează și el
   * (400 dacă motivul lipsește); aici prevenim trimiterea early.
   */
  const submitOverride = async () => {
    setOverrideError(null);
    if (!form?.id) return;
    if (!overrideReason.trim()) {
      setOverrideError('Motivul este obligatoriu pentru un verdict modificat manual.');
      return;
    }
    try {
      setSaving(true);
      const saved = await dermatoApi.screeningResult.upsert(form.id, {
        evaluationSummary: buildEvaluationSummary(abcde, derm, ai, riskFactorsCount),
        screeningConclusion: overrideConclusion,
        recommendation: recommendation || VERDICT_META[overrideConclusion].recommendation,
        observations: observations || undefined,
        referralToBiopsy: effectiveReferralToBiopsy,
        isManualOverride: true,
        overrideReason: overrideReason.trim(),
        evaluatedBy: evaluatedByRef.current || user?.email || 'medic',
      });
      setScreening(saved);
      setOverrideModalOpen(false);
      setOverrideReason('');
      message.success('Verdict suprascris manual și salvat.');
    } catch (e: any) {
      const apiMsg = e?.response?.data?.message;
      if (Array.isArray(apiMsg)) {
        setOverrideError(apiMsg.join(', '));
      } else {
        setOverrideError(apiMsg ?? 'Nu s-a putut salva override-ul.');
      }
    } finally {
      setSaving(false);
    }
  };

  const openOverrideModal = () => {
    if (screening?.isManualOverride) {
      // pre-populează cu valorile curente
      setOverrideConclusion(screening.screeningConclusion);
      setOverrideReason(screening.overrideReason ?? '');
    } else {
      setOverrideConclusion(ScreeningConclusion.SUSPECT);
      setOverrideReason('');
    }
    setOverrideError(null);
    setOverrideModalOpen(true);
  };

  /**
   * D2.3 — Trimite la biopsie: creează DOAR un draft de biopsie (fără reguli
   * clinice; acelea se aplică la submit/finalize). Backend-ul marchează
   * automat referralToBiopsy=true pe screening result.
   */
  const sendToBiopsy = async () => {
    if (!form?.id || !lesion?.id) {
      message.warning('Selectează o leziune înainte de a trimite la biopsie.');
      return;
    }
    Modal.confirm({
      title: 'Trimite la biopsie',
      content: `Se va crea un draft de biopsie pentru leziunea ${lesion.clinicalCode} (${lesion.location}). Poți completa detaliile ulterior în pasul 9.`,
      okText: 'Creează draft',
      cancelText: 'Renunță',
      onOk: async () => {
        try {
          setCreatingBiopsy(true);
          const biopsy = await dermatoApi.screeningResult.createBiopsyDraft(form.id, lesion.id, {
            procedureType: undefined,
            performedBy: evaluatedByRef.current || user?.email || undefined,
            sampleType: 'Piesa cutanata',
            laboratory: 'Necompletat (draft)',
            observations: 'Draft creat din Step 8 — Trimite la biopsie.',
          });
          message.success(`Draft biopsie creat (${biopsy.sampleCode}). Deschide pasul 9 pentru a completa.`);
          // Reîncarcă screening pt a reflecta referralToBiopsy=true
          const refreshed = await dermatoApi.screeningResult.getByForm(form.id);
          setScreening(refreshed);
        } catch (e: any) {
          const apiMsg = e?.response?.data?.message;
          message.error(Array.isArray(apiMsg) ? apiMsg.join(', ') : (apiMsg ?? 'Nu s-a putut crea draft-ul de biopsie.'));
        } finally {
          setCreatingBiopsy(false);
        }
      },
    });
  };

  useRegisterStepSave(persist);

  if (loading) {
    return <Card loading title="Rezultat screening" />;
  }

  // 2.9.3 — episodul exclus din program (refuz înregistrat la înrolare):
  // nu se mai calculează un verdict clinic, ci se afișează starea de excludere.
  if (screening?.screeningConclusion === ScreeningConclusion.SCREENING_REFUZAT) {
    return (
      <Card title="Rezultat screening">
        <Alert
          type="error"
          showIcon
          message="EXCLUS DIN PROGRAM"
          description="Pacientul a refuzat participarea la programul de screening (starea SCREENING REFUZAT). Episodul a fost închis ca refuzat — nu se mai calculează un verdict clinic."
        />
      </Card>
    );
  }

  if (lesions.length === 0) {
    return (
      <Card title="Rezumat screening">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical">
              <Text>Nu există leziuni înregistrate — screening fără leziuni.</Text>
              <Text type="secondary">
                Dacă examinarea nu a evidențiat leziuni, poți finaliza formularul. Pașii 9–15 (per leziune) rămân necompletați.
              </Text>
            </Space>
          }
        >
          <Space wrap>
            <Button onClick={() => patient && navigate(`/patients/${patient.id}/dermato-wizard/3`)}>
              Înapoi la leziuni
            </Button>
            <Button
              type="primary"
              data-testid="no-lesions-finalize"
              onClick={() => setConfirmFinalize(true)}
            >
              Finalizează fără leziuni
            </Button>
          </Space>
        </Empty>
        {screening?.returnStatus && (
          <Alert
            type="success"
            showIcon
            style={{ marginTop: 16 }}
            message="Status de revenire"
            description={`Recontrol programat la ${formatRecontrolDate(screening.nextRecontrolDate)}${
              screening.recontrolIntervalMonths ? ` (peste ${screening.recontrolIntervalMonths} luni)` : ''
            }.`}
          />
        )}
        <Modal
          title="Finalizezi formularul fără leziuni?"
          open={confirmFinalize}
          okText="Da, finalizează"
          cancelText="Anulează"
          confirmLoading={finalizing}
          onCancel={() => setConfirmFinalize(false)}
          onOk={async () => {
            if (!form?.id || !patient) return;
            setFinalizing(true);
            try {
              const result = await dermatoApi.finalizeNoLesions(form.id, {
                recontrolIntervalMonths: recontrolMonths,
              });
              message.success(
                `Screening finalizat fără leziuni. Scrisoarea medicală a fost generată (${result.letter.fileName}); ` +
                  `recontrol recomandat la ${formatRecontrolDate(result.nextRecontrolDate)}.`,
              );
              navigate(`/patients/${patient.id}`);
            } catch (e: any) {
              const apiMsg = e?.response?.data?.message;
              message.error(
                Array.isArray(apiMsg) ? apiMsg.join(', ') : (apiMsg ?? 'Finalizarea fără leziuni a eșuat.'),
              );
            } finally {
              setFinalizing(false);
              setConfirmFinalize(false);
            }
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              Se consemnează verdictul „Fără leziuni", se generează scrisoarea medicală către medicul de
              familie, iar formularul este trimis spre revizuire (read-only).
            </Text>
            <Space>
              <Text>Recontrol recomandat peste</Text>
              <Select
                data-testid="no-lesions-recontrol-interval"
                value={recontrolMonths}
                onChange={setRecontrolMonths}
                style={{ width: 120 }}
                options={[
                  { value: 6, label: '6 luni' },
                  { value: 12, label: '12 luni' },
                  { value: 24, label: '24 luni' },
                ]}
              />
            </Space>
          </Space>
        </Modal>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          📊 Rezumat screening — per leziune
        </Space>
      }
      extra={
        <Space>
          {loadingScreening && <Spin size="small" />}
          <Button
            icon={<EditOutlined />}
            onClick={openOverrideModal}
            disabled={!lesion || summary.verdict === 'INCONCLUSIVE'}
          >
            Suprascrie manual
          </Button>
          <Button
            type="primary"
            icon={<ExperimentOutlined />}
            onClick={sendToBiopsy}
            loading={creatingBiopsy}
            disabled={!lesion}
          >
            Trimite la biopsie
          </Button>
        </Space>
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {loadingData && <Spin tip="Se calculează rezumatul…" />}

      {!lesion && !loadingData && (
        <Alert type="warning" showIcon message="Selectează o leziune pentru a vedea rezumatul screening-ului." />
      )}

      {lesion && !loadingData && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text strong>{lesion.location}</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          {screening?.isManualOverride && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                <Space direction="vertical" size={2}>
                  <Text strong>Verdict suprascris manual de medic</Text>
                  <Text type="secondary">Motiv: {screening.overrideReason}</Text>
                  <Text type="secondary">Modificat la {new Date(screening.evaluatedAt).toLocaleString('ro-RO')}</Text>
                </Space>
              }
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
            <Card type="inner" title="Rezumat evaluări">
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Anamneză / Risc">
                  <Tag color={summary.riskFactors > 2 ? 'red' : summary.riskFactors > 0 ? 'orange' : 'green'}>
                    {summary.riskFactors} factori de risc
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="ABCDE">
                  {abcde ? (
                    <Tag color={abcde.conclusion === 'SUSPICIOUS_LESION' ? 'red' : abcde.conclusion === 'REQUIRES_DERMOSCOPY' ? 'orange' : 'green'}>
                      {abcde.conclusion.replace(/_/g, ' ')}
                    </Tag>
                  ) : <Text type="secondary">Necompletat</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Dermatoscopie">
                  {derm ? (
                    <Tag color={derm.result === 'SUSPICIOUS_LESION' ? 'red' : 'green'}>
                      {derm.result.replace(/_/g, ' ')}
                    </Tag>
                  ) : <Text type="secondary">Necompletat</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Videodermatoscopie / AI">
                  {ai ? (
                    <Space>
                      <Tag color={ai.aiClassification === 'RISK_HIGH' ? 'red' : ai.aiClassification === 'RISK_MODERATE' ? 'orange' : 'green'}>
                        {ai.aiClassification.replace(/_/g, ' ')}
                      </Tag>
                      <Text type="secondary">({ai.aiProbability}%)</Text>
                    </Space>
                  ) : <Text type="secondary">Necompletat</Text>}
                </Descriptions.Item>
                <Descriptions.Item label="Total Body">
                  <Tag color="default">{lesion.status.replace(/_/g, ' ')}</Tag>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card type="inner" title="Rezultat final screening">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Tag color={effectiveMeta.color} style={{ fontSize: 16, padding: '6px 12px' }} icon={effectiveMeta.icon as any}>
                  {effectiveMeta.label}
                </Tag>
                <Paragraph style={{ marginBottom: 0 }}>{recommendation || effectiveMeta.recommendation}</Paragraph>
                <Divider style={{ margin: '8px 0' }} />

                <Form.Item label="Recomandare" style={{ marginBottom: 8 }}>
                  <Input.TextArea
                    rows={2}
                    value={recommendation}
                    onChange={(e) => setRecommendation(e.target.value)}
                    placeholder={VERDICT_META[summary.verdict].recommendation}
                  />
                </Form.Item>
                <Form.Item label="Observații" style={{ marginBottom: 8 }}>
                  <Input.TextArea
                    rows={2}
                    value={observations}
                    onChange={(e) => setObservations(e.target.value)}
                    placeholder="Observații medicale suplimentare…"
                  />
                </Form.Item>

                {screening && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Salvat la {new Date(screening.evaluatedAt).toLocaleString('ro-RO')} de {screening.evaluatedBy}.
                    Se actualizează la „Salvează draft" sau „Salvează și continuă".
                  </Text>
                )}
                {!screening && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Concluzia se calculează dinamic din pașii 1-7. Apasă „Salvează draft" pentru a o înregistra.
                  </Text>
                )}
              </Space>
            </Card>
          </div>
        </>
      )}

      <Modal
        title="Suprascrie manual verdictul screening-ului"
        open={overrideModalOpen}
        onCancel={() => setOverrideModalOpen(false)}
        onOk={submitOverride}
        okText="Salvează override"
        cancelText="Renunță"
        confirmLoading={saving}
        okButtonProps={{ danger: true }}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Override-ul se justifică și se memorează în audit. Motivul este OBLIGATORIU."
          />
          <Form.Item label="Concluzie aleasă de medic" required style={{ marginBottom: 0 }}>
            <Select
              value={overrideConclusion}
              onChange={setOverrideConclusion}
              options={CONCLUSION_OPTIONS}
            />
          </Form.Item>
          <Form.Item label="Motiv" required style={{ marginBottom: 0 }}>
            <Input.TextArea
              rows={3}
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Ex: tablou clinic benign dar pacient cu istoric familial puternic — supraveghere activă"
            />
          </Form.Item>
          {overrideError && <Alert type="error" showIcon message={overrideError} />}
        </Space>
      </Modal>
    </Card>
  );
};

export default Step8ScreeningResult;
